import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { CommonPagePromo } from 'components/no-auth/common/CommonPagePromo';
import { LightButton } from 'components/no-auth/common/fromAuth/LightButton/LightButton';
import { LightDangerouslyHtml } from 'components/no-auth/common/fromAuth/LightDangerouslyHtml/LightDangerouslyHtml';
import { LightDiskButton } from 'components/no-auth/common/fromAuth/LightDiskButton/LightDiskButton';
import { LightHeaderPanel } from 'components/no-auth/common/fromAuth/LightHeaderPanel/LightHeaderPanel';
import { MobileTextComponent, VoteBlock } from 'components/no-auth/help/styles';
import NotAuthBasePage from 'components/no-auth/NotAuthBasePage';
import { getHelpCenterUrl, getZendeskLocale, useGetQueryParams } from 'components/no-auth/utils';

import { toggleSpinner, toggleSnackBar } from 'actions/appAction';
import { toggleChat } from 'actions/chatAction';

import { GET_CHAT_CLICKED } from 'constants/amplitude/AmplitudeEvents';
import { ARTICLE, SECTION } from 'constants/amplitude/AmplitudeParamName';

import { getLangCountryPrefix } from 'helpers/utils';

import amplitude from 'amplitude-js';

import { LightText } from '../../components/no-auth/common/fromAuth/LightText/LightText';

const ZendeskHelpMobileArticlePage = (props) => {
    const [voteVal, setVoteVal] = useState(0);

    const article = props.article;
    const url = `${getLangCountryPrefix(props.lang, props.country)}/help-mobile`;
    const location = useLocation();
    const urlParams = useGetQueryParams();
    const isMobilePlatform = urlParams.platform === 'ios' || urlParams.platform === 'android';
    const isAnonymousChat = urlParams.anonymousChat === 'true';
    const userEmail = urlParams.email;
    const voteInfo = props.voteInfo;

    useEffect(() => {
        if (voteVal === 0) {
            setVoteVal(voteInfo?.value);
        }
    }, [voteVal]);

    const chatLink = () => {
        if (location.pathname.includes('showChat')) {
            return getHelpCenterUrl(`${location.pathname}`, urlParams);
        }
        return getHelpCenterUrl(`${location.pathname}/showChat`, urlParams);
    };

    const handleToggleChat = () => {
        if (!urlParams.platform) {
            props.toggleChat(true);
        }
    };

    const hasDirectLinkLabel = article?.label_names?.includes('directLink');

    const vote = async (isUpVote) => {
        if (isUpVote) {
            setVoteVal(1);
        } else if (!isUpVote) {
            setVoteVal(-1);
        }
        const response = await fetch('/api/zendesk/vote', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                articleId: article.id,
                userEmail: userEmail,
                locale: getZendeskLocale(props.lang, props.country),
                isUpVote: isUpVote,
            }),
        });
        props.toggleSnackBar('true', props.snackbarMessage);
        return response.ok;
    };

    const canShowVoteBlock = (voteInfo) => {
        if (isMobilePlatform) {
            return userEmail;
        }
        return voteInfo && voteInfo.authorized;
    };

    const canShowChatBlock = (voteInfo, voteVal, isAnonymousChat) => {
        if (isMobilePlatform && userEmail) {
            return voteVal === -1; //кейс с новым приложением (показываем чат - если клиент 👎)
        } else if (isMobilePlatform) {
            return true; //кейс со старым приложением (не поддерживает голосование - всегда показываем чат)
        } else if (isAnonymousChat) {
            return true;
        }
        return voteVal === -1; //кейс web (показываем чат - если есть данные о голосовании)
    };

    return (
        <CommonPagePromo
            useThemeColor
            backUrl={hasDirectLinkLabel ? null : getHelpCenterUrl(`${url}/category/${props.category.id}/section/${props.article.section_id}`, urlParams)}
            type={'tagA'}
            rightHeaderComponent={
                !urlParams.platform ? (
                    <LightDiskButton
                        type={'tagA'}
                        href={props.backUrl}
                        onClick={() => {
                            props.toggleSpinner(true);
                            sessionStorage?.removeItem('prevUrl');
                        }}
                        iconName={'cross'}
                        useThemeColor
                    />
                ) : null
            }
        >
            <LightHeaderPanel useThemeColor title={article?.title} />
            <MobileTextComponent>
                <LightDangerouslyHtml dangerouslyHtml={article?.body} />
            </MobileTextComponent>
            {canShowVoteBlock(voteInfo) && (
                <VoteBlock>
                    <LightText color={'mediumGray'}>{props.opinionText}</LightText>
                    <div>
                        <LightDiskButton
                            iconName={'thumbs-up'}
                            iconColor={voteVal === 1 ? 'white' : 'purple'}
                            type={'button'}
                            variant={voteVal === 1 ? 'primary' : 'secondary_filled'}
                            raised={false}
                            onClick={async () => await vote(true)}
                        />
                        <LightDiskButton
                            iconName={'thumbs-down'}
                            iconColor={voteVal === -1 ? 'white' : 'purple'}
                            type={'button'}
                            variant={voteVal === -1 ? 'primary' : 'secondary_filled'}
                            raised={false}
                            onClick={async () => await vote(false)}
                        />
                    </div>
                </VoteBlock>
            )}
            {canShowChatBlock(voteInfo, voteVal, isAnonymousChat) && (
                <LightButton
                    type={urlParams.platform ? 'link' : null}
                    href={urlParams.platform ? chatLink() : null}
                    useThemeColor
                    caption={props.chatButtonCaption}
                    variant={'row'}
                    textCenter
                    negativeMargin
                    onClick={() => {
                        if (!isMobilePlatform) {
                            amplitude.getInstance().logEvent(GET_CHAT_CLICKED, { [ARTICLE]: article.id, [SECTION]: article.section_id });
                        }
                        handleToggleChat();
                    }}
                />
            )}
        </CommonPagePromo>
    );
};

const mapStateToProps = ({ extra }) => ({
    chatButtonCaption: extra?.strapiApi?.chat_btn_mobile,
    opinionText: extra?.strapiApi?.opinion_text,
    category: extra?.zendeskHelpData?.category,
    sections: extra?.zendeskHelpData?.sections,
    article: extra?.zendeskHelpData?.article,
    voteInfo: extra?.zendeskHelpData?.voteInfo,
    lang: extra.noAuthFooter?.language?.lang,
    country: extra.noAuthFooter?.language?.country,
    snackbarMessage: extra.noAuthFooter?.messages?.snackbarMessage,
});

export default connect(mapStateToProps, { toggleChat, toggleSpinner, toggleSnackBar })(NotAuthBasePage(ZendeskHelpMobileArticlePage));
